<template>
  <div class="seiko-matrix-scroll" :style="variablesCSS">
    <table
      :id="idTabla"
      class="seiko-matrix"
      :class="{
        'fija-izquierda': stickyActivo,
        'ancho-fijo': layoutFijoSeguro,
        compacta: compacta,
        clicable: !!$listeners['click:fila'],
      }"
    >
      <!-- Con table-layout: fixed el navegador toma los anchos de la primera fila, y las
           columnas del medio van en la segunda: sin esto se reparten el espacio por igual. -->
      <colgroup v-if="anchosPorColgroup && layoutFijoSeguro">
        <col v-for="columna in columnasIzquierda" :key="`cg-i-${columna.valor}`" :style="estiloColumna(columna)" />
        <col v-for="columna in columnas" :key="`cg-m-${columna.clave}`" :style="estiloColumna(columna)" />
        <col v-for="columna in columnasDerecha" :key="`cg-d-${columna.valor}`" :style="estiloColumna(columna)" />
      </colgroup>
      <thead>
        <tr>
          <th
            v-for="(columna, indice) in columnasIzquierda"
            :key="`fija-${columna.valor}`"
            :rowspan="nivelesEncabezado"
            :class="{
              'celda-fija': stickyActivo,
              'text-left': columna.alineacionEncabezado === 'left',
              'text-wrap': !!columna.ajustarTexto,
            }"
            :style="estiloColumnaIzquierda(columna, indice)"
            :title="columna.titulo"
          >
            <slot name="encabezado-izquierda" :columna="columna">{{ columna.texto }}</slot>
          </th>

          <th v-if="columnas.length && titulo" :colspan="columnas.length">
            {{ titulo }}
          </th>

          <th
            v-for="columna in columnasDerecha"
            :key="`resumen-${columna.valor}`"
            :rowspan="nivelesEncabezado"
            :style="estiloColumna(columna)"
            :title="columna.titulo"
          >
            {{ columna.texto }}
          </th>
        </tr>

        <tr v-if="grupos.length">
          <th
            v-for="(grupo, indice) in grupos"
            :key="`grupo-${indice}-${grupo.nombre}`"
            :colspan="grupo.cantidad"
          >
            {{ grupo.nombre }}
          </th>
        </tr>

        <tr v-if="columnas.length">
          <th
            v-for="columna in columnas"
            :key="`col-${columna.clave}`"
            :class="columna.clase"
            :title="columna.titulo"
            :style="estiloColumna(columna)"
          >
            <slot name="encabezado" :columna="columna">{{ columna.texto }}</slot>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="fila in filasVisibles"
          :key="fila[claveFila]"
          :class="[
            { 'fila-seleccionada': fila[claveFila] === filaSeleccionada },
            claseFila ? claseFila(fila) : null,
          ]"
          @click="$emit('click:fila', fila)"
        >
          <td
            v-for="(columna, indice) in columnasIzquierda"
            :key="`fija-${columna.valor}`"
            :class="[clasesCelda(columna), { 'celda-fija': stickyActivo }]"
            :style="estiloColumnaIzquierda(columna, indice)"
          >
            <slot
              :name="`celda.${columna.valor}`"
              :fila="fila"
              :columna="columna"
            >
              <span
                v-if="columna.anchoMaximo"
                class="celda-truncada"
                :style="{ maxWidth: columna.anchoMaximo }"
                :title="fila[columna.valor]"
                >{{ fila[columna.valor] }}</span
              >
              <template v-else>{{ fila[columna.valor] }}</template>
            </slot>
          </td>

          <td
            v-for="columna in columnas"
            :key="`col-${columna.clave}`"
            :class="columna.clase"
          >
            <slot name="celda" :fila="fila" :columna="columna">
              {{ fila[columna.clave] }}
            </slot>
          </td>

          <td
            v-for="columna in columnasDerecha"
            :key="`resumen-${columna.valor}`"
            :class="clasesCelda(columna)"
          >
            <slot
              :name="`celda.${columna.valor}`"
              :fila="fila"
              :columna="columna"
            >
              <span
                v-if="columna.anchoMaximo"
                class="celda-truncada"
                :style="{ maxWidth: columna.anchoMaximo }"
                :title="fila[columna.valor]"
                >{{ fila[columna.valor] }}</span
              >
              <template v-else>{{ fila[columna.valor] }}</template>
            </slot>
          </td>
        </tr>
        <tr v-if="textoVacio && !filas.length" class="fila-vacia">
          <td :colspan="totalColumnas">{{ textoVacio }}</td>
        </tr>
      </tbody>
    </table>

    <div v-if="paginacionActiva && totalPaginas > 1 && !opcionesPorPagina.length" class="seiko-matrix-paginacion">
      <button
        type="button"
        class="seiko-matrix-paginacion__btn"
        :disabled="paginaActual <= 1"
        @click="irAPaginaAnterior"
      >
        ‹ Anterior
      </button>
      <span class="seiko-matrix-paginacion__info">
        Página {{ paginaActual }} de {{ totalPaginas }}
      </span>
      <button
        type="button"
        class="seiko-matrix-paginacion__btn"
        :disabled="paginaActual >= totalPaginas"
        @click="irAPaginaSiguiente"
      >
        Siguiente ›
      </button>
    </div>

    <!-- Pie de paginación completo (opt-in con opciones-por-pagina) -->
    <div v-if="opcionesPorPagina.length && filas.length" class="seiko-matrix-pie">
      <label class="seiko-matrix-pie__tamano">
        <span>Filas por página</span>
        <select :value="tamanoEfectivo" @change="cambiarTamano(Number($event.target.value))">
          <option v-for="opcion in opcionesPorPagina" :key="opcion" :value="opcion">
            {{ opcion === 0 ? "Todas" : opcion }}
          </option>
        </select>
      </label>

      <span class="seiko-matrix-pie__rango">
        <strong>{{ rangoDesde }}–{{ rangoHasta }}</strong> de <strong>{{ filas.length }}</strong>
      </span>

      <nav v-if="totalPaginas > 1" class="seiko-matrix-pie__paginas" aria-label="Paginación">
        <button type="button" class="seiko-matrix-pie__btn" :disabled="paginaActual <= 1" title="Primera página" @click="irAPagina(1)">«</button>
        <button type="button" class="seiko-matrix-pie__btn" :disabled="paginaActual <= 1" title="Página anterior" @click="irAPaginaAnterior">‹</button>
        <template v-for="(item, i) in paginasVisibles">
          <span v-if="item === null" :key="'p-' + i" class="seiko-matrix-pie__puntos">…</span>
          <button
            v-else
            :key="'p-' + i"
            type="button"
            :class="['seiko-matrix-pie__btn', { 'seiko-matrix-pie__btn--activo': item === paginaActual }]"
            :aria-current="item === paginaActual ? 'page' : null"
            @click="irAPagina(item)"
          >{{ item }}</button>
        </template>
        <button type="button" class="seiko-matrix-pie__btn" :disabled="paginaActual >= totalPaginas" title="Página siguiente" @click="irAPaginaSiguiente">›</button>
        <button type="button" class="seiko-matrix-pie__btn" :disabled="paginaActual >= totalPaginas" title="Última página" @click="irAPagina(totalPaginas)">»</button>
      </nav>
    </div>
  </div>
</template>

<script>
/**
 * Tabla matricial genérica: columnas fijas a la izquierda, un bloque de
 * columnas dinámicas con encabezado jerárquico en el medio y columnas de
 * resumen a la derecha. Scrollea horizontalmente sin romper la página.
 *
 * Es el equivalente en HTML crudo de `BaseDataTable` (comparte sus variables
 * CSS, su radio y su sombra) para los casos en que `v-data-table` no alcanza:
 * encabezados de varios niveles con rowspan/colspan y columnas que se descubren
 * en tiempo de ejecución.
 *
 * Ejemplo (planilla de asistencia):
 *   columnasIzquierda: [{ valor: 'nro', texto: 'N°' }, ...]
 *   titulo:            'FECHAS DE CLASES EFECTIVAS'
 *   grupos:            [{ nombre: 'AGOSTO', cantidad: 2 }, ...]
 *   columnas:          [{ clave: '25-08-25', texto: 25 }, ...]
 *   columnasDerecha:   [{ valor: 'asistido', texto: 'ASISTIDO' }, ...]
 *
 * Una columna dinámica puede además traer `clase` y `titulo`; `.columna-atenuada`
 * es parte de la API de este componente para las columnas sin datos propios. Se
 * define acá y no en el consumidor porque el `scoped` del padre no alcanza a
 * estas celdas, y resolverlo con `::v-deep` lo ataría al DOM interno de la tabla.
 */
export default {
  name: "BaseMatrixTable",

  props: {
    /**
     * Columnas fijas de la izquierda:
     * { valor, texto, ancho?, anchoMinimo?, anchoMaximo?, alineacion?, destacada?, titulo?,
     *   ajustarTexto?, alineacionEncabezado? }
     * `ajustarTexto` deja que el texto largo salte de línea (por defecto las celdas
     * no parten); `alineacionEncabezado: 'left'` alinea también el rótulo.
     * Con `anchoMaximo` el texto se recorta con puntos suspensivos y el valor
     * completo queda en el `title` de la celda. `titulo` (aparte) es el tooltip
     * nativo del encabezado de la columna.
     */
    columnasIzquierda: {
      type: Array,
      default: () => [],
    },
    /** Encabezado que corona a todas las columnas dinámicas. */
    titulo: {
      type: String,
      default: "",
    },
    /**
     * `id` HTML a aplicar sobre el `<table>` interno (no sobre el `div`
     * que lo envuelve). Existe para exportadores que leen el DOM por id
     * (p. ej. `XLSX.utils.table_to_book`), que necesitan una `<table>` real.
     */
    idTabla: {
      type: String,
      default: null,
    },
    /** Nivel intermedio del encabezado: { nombre, cantidad }. Opcional. */
    grupos: {
      type: Array,
      default: () => [],
    },
    /**
     * Columnas dinámicas: { clave, texto, ancho?, clase?, titulo? }.
     *
     * `clase` se aplica al encabezado y a todas las celdas de esa columna; la
     * tabla expone `columna-atenuada` para el caso de "esta columna existe pero
     * no tiene datos propios". `titulo` es el tooltip del encabezado. Para
     * rotularlo con algo que no sea `texto`, usar el slot `#encabezado`.
     */
    columnas: {
      type: Array,
      default: () => [],
    },
    /**
     * Columnas de resumen de la derecha, con la misma forma que las de la
     * izquierda (`titulo` incluido, para el tooltip del encabezado).
     */
    columnasDerecha: {
      type: Array,
      default: () => [],
    },
    /** Filas ya calculadas: se leen por `columna.valor` o por `columna.clave`. */
    filas: {
      type: Array,
      default: () => [],
    },
    /** Propiedad de la fila que sirve de `key`. */
    claveFila: {
      type: String,
      default: "id",
    },
    /**
     * Valor de `claveFila` de la fila que debe quedar resaltada (por
     * ejemplo, la última en la que se hizo click). El componente emite
     * `click:fila` con la fila completa; quien lo escucha decide si la
     * marca como seleccionada.
     */
    filaSeleccionada: {
      type: [String, Number],
      default: null,
    },
    /**
     * Clase (o clases) CSS extra por fila, a criterio del consumidor —por
     * ejemplo, resaltar la gestión del año en curso en un listado de
     * gestiones. Recibe la fila y devuelve lo mismo que aceptaría un `:class`
     * (string, objeto o array). `null` no agrega nada.
     */
    claseFila: {
      type: Function,
      default: null,
    },
    /**
     * Mantiene visibles las columnas de la izquierda mientras se scrollea en
     * horizontal. Clave en celular, donde solo entran dos o tres columnas.
     * Requiere que todas esas columnas menos la última declaren `ancho`; si
     * falta alguna no se puede calcular el desplazamiento y se ignora.
     */
    fijarColumnasIzquierda: {
      type: Boolean,
      default: false,
    },
    /**
     * Declara los anchos también en un <colgroup>, para que las columnas del medio
     * (`columnas`) respeten su `ancho` con table-layout: fixed. Opt-in: el resto de
     * las tablas conserva el reparto de siempre.
     */
    anchosPorColgroup: {
      type: Boolean,
      default: false,
    },
    /** Tipografía y padding reducidos: para que entre más en pantallas chicas. */
    compacta: {
      type: Boolean,
      default: false,
    },
    headerBgColor: {
      type: String,
      default: "var(--v-neutral-200-base)",
    },
    headerTextColor: {
      type: String,
      default: "var(--v-neutral-800-base)",
    },
    /** Color de fondo de la fila al pasar el cursor. */
    rowHoverColor: {
      type: String,
      default: "var(--v-primary-100-base)",
    },
    /**
     * Cantidad de filas por página. `0` (default) deja el comportamiento de
     * siempre: sin paginar, se muestran todas las `filas`.
     */
    itemsPorPagina: {
      type: Number,
      default: 0,
    },
    /**
     * Opciones del selector "Filas por página" (`0` = Todas). Si se pasa, la tabla
     * muestra un pie completo (selector, rango y páginas numeradas) en vez del
     * paginador simple. `itemsPorPagina` es el valor inicial.
     */
    opcionesPorPagina: {
      type: Array,
      default: () => [],
    },
    /**
     * Mensaje a mostrar en una fila única cuando no hay `filas`. Vacío
     * (default) deja el comportamiento de siempre: el cuerpo queda vacío.
     */
    textoVacio: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      paginaActual: 1,
      tamanoElegido: null,
    };
  },

  watch: {
    /** Si cambian las filas (nuevo filtro de búsqueda, nueva carga) se
     * vuelve a la primera página; si no, un filtro que deja pocos
     * resultados puede dejar la vista en una página vacía. */
    filas() {
      this.paginaActual = 1;
    },
  },

  computed: {
    /** Filas por página en uso (0 = todas): la elegida en el pie o, si no, la del prop. */
    tamanoEfectivo() {
      return this.opcionesPorPagina.length && this.tamanoElegido !== null
        ? this.tamanoElegido
        : this.itemsPorPagina;
    },

    paginacionActiva() {
      return this.tamanoEfectivo > 0;
    },

    totalPaginas() {
      if (!this.paginacionActiva) return 1;
      return Math.max(1, Math.ceil(this.filas.length / this.tamanoEfectivo));
    },

    rangoDesde() {
      if (!this.filas.length) return 0;
      return this.paginacionActiva ? (this.paginaActual - 1) * this.tamanoEfectivo + 1 : 1;
    },

    rangoHasta() {
      if (!this.paginacionActiva) return this.filas.length;
      return Math.min(this.paginaActual * this.tamanoEfectivo, this.filas.length);
    },

    /** Páginas a mostrar: siempre la primera y la última, y las vecinas de la actual. */
    paginasVisibles() {
      const total = this.totalPaginas;
      const actual = this.paginaActual;
      const numeros = new Set([1, total, actual - 1, actual, actual + 1]);
      if (actual <= 3) [2, 3, 4].forEach((n) => numeros.add(n));
      if (actual >= total - 2) [total - 1, total - 2, total - 3].forEach((n) => numeros.add(n));
      const orden = [...numeros].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
      const resultado = [];
      orden.forEach((n, i) => {
        if (i > 0 && n - orden[i - 1] > 1) resultado.push(null);
        resultado.push(n);
      });
      return resultado;
    },

    totalColumnas() {
      return (
        this.columnasIzquierda.length + this.columnas.length + this.columnasDerecha.length || 1
      );
    },

    filasVisibles() {
      if (!this.paginacionActiva) return this.filas;
      const inicio = (this.paginaActual - 1) * this.tamanoEfectivo;
      return this.filas.slice(inicio, inicio + this.tamanoEfectivo);
    },

    variablesCSS() {
      return {
        "--header-bg": this.headerBgColor,
        "--header-text": this.headerTextColor,
        "--border-color": "var(--v-primary-200-base)",
        "--row-alt": "var(--v-neutral-100-base)",
        "--row-hover": this.rowHoverColor,
      };
    },

    /** Cuántas filas ocupa el encabezado: banner + grupos (si hay) + columnas. */
    nivelesEncabezado() {
      if (!this.columnas.length) return 1;
      return this.grupos.length ? 3 : 2;
    },

    /**
     * Posición `left` de cada columna fija, acumulando los anchos declarados.
     * Devuelve null si alguna columna intermedia no declara `ancho`: sin ese
     * dato el CSS no puede ubicarlas y es preferible no fijar nada.
     */
    desplazamientosIzquierda() {
      const desplazamientos = [];
      let acumulado = 0;

      for (let i = 0; i < this.columnasIzquierda.length; i++) {
        desplazamientos.push(acumulado);
        if (i === this.columnasIzquierda.length - 1) break;

        const ancho = parseFloat(this.columnasIzquierda[i].ancho);
        if (!ancho) return null;
        acumulado += ancho;
      }

      return desplazamientos;
    },

    stickyActivo() {
      return (
        this.fijarColumnasIzquierda && this.desplazamientosIzquierda !== null
      );
    },

    /**
     * `table-layout: fixed` (ver estilo `.ancho-fijo`) solo es seguro cuando
     * TODAS las columnas —no solo las fijas de la izquierda, que ya lo
     * garantiza `stickyActivo`— declaran `ancho`. Si alguna columna del medio
     * o de la derecha no lo declara (p. ej. el "DETALLE" de
     * `DesignacionDocenteDAC.vue`, que no tiene anchos fijos), forzar el
     * layout fijo la aprieta a practicamente 0px en vez de dejarla mostrar su
     * contenido, así que en ese caso se deja el layout automático de siempre.
     */
    layoutFijoSeguro() {
      return (
        this.stickyActivo &&
        this.columnas.every((c) => !!c.ancho) &&
        this.columnasDerecha.every((c) => !!c.ancho)
      );
    },
  },

  methods: {
    estiloColumna(columna) {
      return {
        ...(columna.ancho ? { width: columna.ancho } : {}),
        ...(columna.anchoMinimo ? { minWidth: columna.anchoMinimo } : {}),
      };
    },

    estiloColumnaIzquierda(columna, indice) {
      return {
        ...this.estiloColumna(columna),
        ...(this.stickyActivo
          ? { left: `${this.desplazamientosIzquierda[indice]}px` }
          : {}),
      };
    },

    clasesCelda(columna) {
      return {
        "text-left": columna.alineacion === "left",
        "text-wrap": !!columna.ajustarTexto,
        "font-weight-bold": !!columna.destacada,
      };
    },

    irAPagina(numero) {
      this.paginaActual = Math.min(Math.max(1, numero), this.totalPaginas);
    },

    cambiarTamano(tamano) {
      this.tamanoElegido = tamano;
      this.paginaActual = 1;
    },

    irAPaginaAnterior() {
      if (this.paginaActual > 1) this.paginaActual--;
    },

    irAPaginaSiguiente() {
      if (this.paginaActual < this.totalPaginas) this.paginaActual++;
    },
  },
};
</script>

<style scoped>
.seiko-matrix-scroll {
  overflow-x: auto;
  border: 1px solid rgba(0, 34, 69, 0.05);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 34, 69, 0.08);
  transition: box-shadow 0.2s ease;
}

.seiko-matrix-scroll:hover {
  box-shadow: 0 12px 24px rgba(0, 34, 69, 0.12);
}

.seiko-matrix {
  /*
   * `border-collapse: collapse` no combina bien con `position: sticky`
   * (bug de Chromium/WebKit): el fondo opaco de una celda fija se pinta
   * más allá de su caja de layout y tapa uno o dos píxeles de la celda
   * vecina que scrollea, "comiéndose" su primer carácter. Con `separate`
   * cada celda pinta estrictamente dentro de su propia caja. El grillado
   * se arma a mano abajo (borde derecho e inferior en cada celda, más el
   * superior del encabezado y el izquierdo de la primera columna) para
   * no duplicar líneas.
   */
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
  font-size: 0.72rem;
}

.seiko-matrix th,
.seiko-matrix td {
  border-right: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  padding: 4px 6px;
  text-align: center;
  white-space: nowrap;
}

.seiko-matrix thead th {
  border-top: 1px solid var(--border-color);
}

.seiko-matrix th:first-child,
.seiko-matrix td:first-child {
  border-left: 1px solid var(--border-color);
}

/* Recorta el texto que excede `anchoMaximo` en vez de estirar la columna. */
.seiko-matrix .celda-truncada {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.seiko-matrix thead th {
  background-color: var(--header-bg);
  color: var(--header-text);
  font-weight: 600;
  letter-spacing: 0.3px;
  border-bottom: 2px solid var(--border-color);
}

.seiko-matrix tbody tr:nth-child(even) {
  background-color: var(--row-alt);
}

.seiko-matrix.clicable tbody tr {
  cursor: pointer;
}

.seiko-matrix tbody tr:hover {
  background-color: var(--row-hover);
}

/* Fila elegida por última vez con un click: queda marcada aunque el mouse
   se mueva a otro lado, con una barrita de color para ubicarla de un
   vistazo incluso en tablas anchas. */
.seiko-matrix tbody tr.fila-seleccionada,
.seiko-matrix tbody tr.fila-seleccionada:hover {
  background-color: var(--v-primary-200-base);
}

.seiko-matrix tbody tr.fila-seleccionada td:first-child {
  box-shadow: inset 3px 0 0 var(--v-primary-base, #002245);
}

.seiko-matrix td.text-left,
.seiko-matrix th.text-left {
  text-align: left;
}

/* Texto largo: salta de línea en vez de estirar la tabla (ver `ajustarTexto`). */
.seiko-matrix td.text-wrap,
.seiko-matrix th.text-wrap {
  white-space: normal;
  overflow-wrap: anywhere;
}

/* ── Columna sin datos propios ───────────────────────────────────────────
   Parte de la API del componente (ver `columnas[].clase`). Se atenúa en vez de
   colorearse: es información ausente, no un valor en estado de error. La trama
   la distingue de una celda vacía cualquiera sin agregar peso visual. */
.seiko-matrix td.columna-atenuada {
  background-image: repeating-linear-gradient(
    45deg,
    var(--v-neutral-200-base) 0,
    var(--v-neutral-200-base) 1px,
    transparent 1px,
    transparent 5px
  );
  color: var(--v-neutral-500-base);
}

.seiko-matrix thead th.columna-atenuada {
  color: var(--v-neutral-600-base);
  font-weight: 400;
}

/* La trama va por debajo del rayado de fila par y del hover, no encima. */
.seiko-matrix tbody tr:hover td.columna-atenuada {
  background-color: var(--row-hover);
}

/* ── Columnas fijas: quedan ancladas mientras el resto scrollea ──────── */
/*
 * Con `table-layout: auto` (el default), Chrome/Edge calculan mal el ancho
 * de las columnas que NO son fijas cuando el bloque `celda-fija` usa
 * `position: sticky`: en vez de dejar que la tabla crezca más que el
 * contenedor (que es lo que después scrollea), aprietan la primera columna
 * de después del bloque fijo muy por debajo de su `ancho` declarado —así se
 * "come" el primer caracter de la celda (p. ej. CARRERA "FYK" se ve "YK").
 * `table-layout: fixed` fuerza a que cada columna respete el ancho que ya
 * declara `estiloColumna`/`estiloColumnaIzquierda`. Pero ESO solo es seguro
 * si TODAS las columnas (no solo las fijas) declaran `ancho`: si falta en
 * alguna (p. ej. el "DETALLE" de `DesignacionDocenteDAC.vue`), forzarlo la
 * aprieta a casi 0px en vez de dejarla respirar por contenido — así que la
 * clase `ancho-fijo` (ver `layoutFijoSeguro`) solo se agrega cuando de
 * verdad todas la declaran.
 */
.seiko-matrix.ancho-fijo {
  table-layout: fixed;
}

.seiko-matrix.fija-izquierda .celda-fija {
  position: sticky;
}

/* Fondo opaco propio: si no, las columnas dinámicas se ven por debajo. */
.seiko-matrix.fija-izquierda tbody td.celda-fija {
  z-index: 1;
  background-color: var(--v-surface-base, #ffffff);
}

.seiko-matrix.fija-izquierda tbody tr:nth-child(even) td.celda-fija {
  background-color: var(--row-alt);
}

.seiko-matrix.fija-izquierda tbody tr:hover td.celda-fija {
  background-color: var(--row-hover);
}

.seiko-matrix.fija-izquierda tbody tr.fila-seleccionada td.celda-fija,
.seiko-matrix.fija-izquierda tbody tr.fila-seleccionada:hover td.celda-fija {
  background-color: var(--v-primary-200-base);
}

/* El encabezado va por encima del cuerpo fijo. */
.seiko-matrix.fija-izquierda thead th.celda-fija {
  z-index: 3;
  background-color: var(--header-bg);
}

/* Borde que separa el bloque fijo de las columnas que se desplazan. */
.seiko-matrix.fija-izquierda .celda-fija:last-of-type {
  border-right: 2px solid var(--border-color);
}

/* ── Modo compacto ───────────────────────────────────────────────────── */
.seiko-matrix.compacta {
  font-size: 0.625rem;
}

.seiko-matrix.compacta th,
.seiko-matrix.compacta td {
  padding: 3px 4px;
  line-height: 1.25;
}

/* Los rótulos largos de las columnas fijas pueden partirse en varias líneas:
   la celda es alta (cubre las 3 filas del encabezado), así que gana ancho. */
.seiko-matrix.compacta thead th[rowspan] {
  white-space: normal;
  letter-spacing: 0;
  font-size: 0.58rem;
}

/* El día del mes es el dato que más se repite: que respire un poco menos. */
.seiko-matrix.compacta thead tr:last-child th {
  padding-left: 1px;
  padding-right: 1px;
}

/* ── Fila destacada ──────────────────────────────────────────────────────
   Parte de la API del componente (ver `claseFila`): el consumidor puede
   devolver "fila-recomendada" para resaltar una fila sin escribir su
   propio CSS ::v-deep, igual que "columna-atenuada" más arriba. */
.seiko-matrix tbody tr.fila-recomendada {
  background-color: #fff2df;
}

.seiko-matrix tbody tr.fila-recomendada:hover {
  background-color: #ffe6c2;
}

/* ── Sin datos (ver `textoVacio`) ─────────────────────────────────────── */
.seiko-matrix tbody tr.fila-vacia td {
  padding: 14px 8px;
  color: var(--v-neutral-600-base, #6b7785);
  font-style: italic;
}

.seiko-matrix tbody tr.fila-vacia:hover {
  background-color: transparent;
}

/* ── Paginación ───────────────────────────────────────────────────────── */
.seiko-matrix-paginacion {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 16px;
  padding: 10px 12px;
  border-top: 1px solid var(--border-color);
  background-color: var(--v-surface-base, #ffffff);
}

.seiko-matrix-paginacion__btn {
  border: 1px solid var(--v-primary-base, #002245);
  background: transparent;
  color: var(--v-primary-base, #002245);
  border-radius: 999px;
  padding: 4px 14px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.seiko-matrix-paginacion__btn:hover:not(:disabled) {
  background-color: var(--v-primary-base, #002245);
  color: #ffffff;
}

.seiko-matrix-paginacion__btn:disabled {
  opacity: 0.4;
  cursor: default;
}

/* Pie de paginación completo */
.seiko-matrix-pie {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 20px;
  padding: 10px 16px;
  border-top: 1px solid var(--border-color);
  background-color: var(--v-neutral-100-base, #f6f8fb);
  font-size: 0.78rem;
  color: var(--v-neutral-800-base, #33414f);
}

.seiko-matrix-pie__tamano {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.seiko-matrix-pie__tamano select {
  padding: 4px 10px;
  border: 1px solid var(--v-primary-base, #002245);
  border-radius: 999px;
  background-color: #ffffff;
  color: var(--v-primary-base, #002245);
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
}

.seiko-matrix-pie__rango strong {
  color: var(--v-primary-base, #002245);
}

.seiko-matrix-pie__paginas {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.seiko-matrix-pie__btn {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: var(--v-primary-base, #002245);
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.seiko-matrix-pie__btn:hover:not(:disabled):not(.seiko-matrix-pie__btn--activo) {
  background-color: rgba(0, 34, 69, 0.08);
}

.seiko-matrix-pie__btn--activo {
  background-color: var(--v-primary-base, #002245);
  color: #ffffff;
  cursor: default;
}

.seiko-matrix-pie__btn:disabled {
  opacity: 0.35;
  cursor: default;
}

.seiko-matrix-pie__puntos {
  padding: 0 4px;
  color: #7a8794;
}

.seiko-matrix-paginacion__info {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--v-neutral-800-base, #33414f);
}
</style>
